import { useEffect } from "react";
import { Github } from "lucide-react";
import Nav from "../components/site/Nav";

const deliverables = [
  {
    label: "State Reports",
    body: "Individual State of Competition reports in each of the five surveyed states, analyzing electoral competition using our suite of metrics — against the backdrop of redistricting and other election-policy changes, demographic shifts, and other large- and small-scale developments over time.",
  },
  {
    label: "District & Statewide Results",
    body: "A sortable database of district and statewide election results (primary, general, and special elections) for state legislative, congressional, and state executive races in selected states.",
  },
  {
    label: "Precinct-Level Results",
    body: "A sortable database of precinct-level election results (primary, general, and special elections) for state legislative, congressional, and state executive races, along with shapefiles.",
  },
  {
    label: "Partisan Lean Calculations",
    body: "A sortable database of resulting partisan lean calculations of state legislative and congressional districts.",
  },
  {
    label: "Competition Metrics",
    body: "The computation of, and key findings related to, the competition metrics for each surveyed state across each selected year and each primary and general election.",
  },
];

export default function ProjectPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="project-page"
      className="min-h-screen bg-[var(--bg)] text-[var(--ink)] font-sans"
    >
      <Nav />

      {/* Page header */}
      <section
        data-testid="project-header"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-6 pb-20 md:pt-8 md:pb-28">
          <h1
            data-testid="project-title"
            className="font-serif text-stone-900 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
          >
            The State of Competition
          </h1>
          <p
            data-testid="project-lead"
            className="mt-10 font-serif text-stone-800 text-2xl sm:text-3xl leading-snug max-w-3xl"
          >
            The Election Competition Center is developing the most
            comprehensive dataset on electoral competition assembled to date.
          </p>
          <p className="mt-8 text-stone-700 leading-relaxed text-[15px] sm:text-base max-w-3xl">
            In 2026, we are focused on tracking and analyzing historical levels
            of electoral competition in five states. An interactive data
            dashboard will allow users to compare fifteen competition metrics
            within and between states. By 2028, we aim to have collected robust
            electoral competition data in all fifty states.
          </p>
        </div>
      </section>

      {/* Deliverables */}
      <section
        data-testid="deliverables-section"
        className="border-b border-stone-300 bg-stone-100/30"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-6 pb-24 md:pt-8 md:pb-32">
          <p className="font-mono-label text-stone-500 mb-8">Deliverables</p>

          <ol
            data-testid="deliverables-list"
            className="border-t border-stone-300"
          >
            {deliverables.map((d, i) => (
              <li
                key={d.label}
                data-testid={`deliverable-${i}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 py-10 border-b border-stone-300"
              >
                <div className="md:col-span-1 font-mono-label text-stone-500">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="md:col-span-4">
                  <h3 className="font-serif text-stone-900 text-2xl leading-tight">
                    {d.label}
                  </h3>
                </div>
                <div className="md:col-span-7">
                  <p className="text-stone-700 leading-relaxed text-[15px]">
                    {d.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          {/* GitHub box (display only) */}
          <div
            data-testid="deliverables-github-box"
            className="mt-16 flex flex-col sm:flex-row sm:items-center gap-6 border border-stone-300 bg-stone-900 text-stone-100 px-8 py-8"
          >
            <Github className="w-8 h-8 shrink-0" strokeWidth={1.5} />
            <p className="font-serif text-stone-50 text-2xl leading-snug">
              All ECC databases are open source and publicly accessible.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
